/**
 * Landing pages runtime (sections/landing-*.liquid).
 *
 * One small flavour store drives every section on the page:
 *  - the buy section's native <variant-picker> (theme) publishes `on:variant:change`
 *  - the lab-results flavour selector and the lot lookup publish too
 *  - subscribers: packshot / buy labels, report facsimile + annotations,
 *    nutrition panel, results selector.
 *
 * Cart, price, availability and dynamic checkout are handled by the theme's own
 * <product-form>, <variant-picker>, <quantity-wrapper> and <buy-buttons> — this
 * file only reacts to their events.
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const keyOf = (s) => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const fmt1 = (n) => (Math.round(Number(n) * 10) / 10).toFixed(1);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const readJson = (el) => {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  /* ------------------------------------------------------------------ store */
  const store = {
    key: null,
    label: '',
    set(key, label, source) {
      key = keyOf(key);
      if (!key || key === this.key) return;
      this.key = key;
      this.label = label || this.label;
      document.dispatchEvent(new CustomEvent('landing:flavour:change', {
        detail: { key, label: this.label, source }
      }));
    }
  };
  window.landing = window.landing || {};
  window.landing.flavour = store;

  const onFlavour = (fn, root) => {
    const opts = root ? { signal: lifecycleFor(root).ac.signal } : undefined;
    document.addEventListener('landing:flavour:change', (e) => fn(e.detail), opts);
  };

  /* ----------------------------------------------------- scroll ticker */
  // One timestamp-throttled, synchronous scroll handler (no requestAnimationFrame: it can be
  // suspended in embedded webviews, which would silently drop every tick). Reveals and the
  // mobile sticky bar both hang off it.
  const tickers = [];
  const runTickers = () => tickers.forEach((fn) => fn());
  let lastTick = 0;
  let trailing = 0;
  const onScrollTick = () => {
    const now = Date.now();
    if (now - lastTick > 80) { lastTick = now; runTickers(); }
    clearTimeout(trailing);
    trailing = setTimeout(runTickers, 120);
  };
  window.addEventListener('scroll', onScrollTick, { passive: true });
  window.addEventListener('resize', onScrollTick, { passive: true });

  /* ------------------------------------------------------------ reveals */
  // Fail-safe: content is visible by default. The hidden/reveal state only exists once
  // `html.rv-ready` is set here, so a script failure can never blank the page.
  const initReveals = () => {
    const els = document.querySelectorAll('.ld [data-rv]');
    if (!els.length || reduceMotion) return;
    const sweep = () => {
      document.querySelectorAll('.ld [data-rv]:not(.rv-in)').forEach((e) => {
        if (e.getBoundingClientRect().top < window.innerHeight * 0.9) e.classList.add('rv-in');
      });
    };
    const arm = () => {
      document.documentElement.classList.add('rv-ready');
      sweep();
      setTimeout(sweep, 600);
      setTimeout(sweep, 1500);
    };
    if (!initReveals.armed) {
      initReveals.armed = true;
      tickers.push(sweep);
      // Arm only once web fonts have settled: flipping the reveal state mid font-load leaves
      // Chrome with stale `ch`-based widths on the revealed elements.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(arm, arm);
      else arm();
    } else {
      sweep();
    }
  };

  /* -------------------------------------------------------------- header */
  const initHeader = (root) => {
    const section = root.closest('.shopify-section') || root;
    const announce = root.querySelector('.ld-announce');
    const measure = () => {
      const h = (announce ? announce.offsetHeight : 0) + 'px';
      section.style.setProperty('--ld-announce-h', h);
      document.documentElement.style.setProperty('--ld-announce-h', h); // the hero's first-screen height reads it too
    };
    measure();
    window.addEventListener('resize', measure, { passive: true });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  };

  /* ------------------------------------------------------- shared helpers */
  // Per-root lifecycle so theme-editor section reloads never accumulate listeners (F15).
  const lifecycles = new Map();
  const lifecycleFor = (root) => {
    const section = root.closest('.shopify-section') || root;
    let lc = lifecycles.get(section);
    if (!lc) {
      lc = { ac: new AbortController(), tickers: [] };
      lifecycles.set(section, lc);
    }
    return lc;
  };
  const addTicker = (root, fn) => { lifecycleFor(root).tickers.push(fn); tickers.push(fn); };
  const teardown = (section) => {
    const lc = lifecycles.get(section);
    if (!lc) return;
    lc.ac.abort();
    lc.tickers.forEach((fn) => { const i = tickers.indexOf(fn); if (i > -1) tickers.splice(i, 1); });
    lifecycles.delete(section);
  };
  const initOnce = (root, key) => {
    if (root.dataset[key]) return false;
    root.dataset[key] = '1';
    return true;
  };

  // One analytics publisher (Shopify Customer Events). Bounded payloads, no personal data.
  // A consumer (custom pixel) must be configured in admin for these to reach any destination.
  const publish = (name, payload) => {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, payload || {});
      }
    } catch (e) { /* analytics must never break the page */ }
  };
  window.landing.publish = publish;

  const money = (cents, currency) => {
    const cur = currency || 'AUD';
    try { return new Intl.NumberFormat(cur === 'AUD' ? 'en-AU' : undefined, { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  };

  const drawerOpen = () => {
    const overlay = document.querySelector('.js-overlay.is-visible');
    const monster = document.querySelector('#monster-cart-wrapper:not(.mu_closed)');
    const native = document.querySelector('cart-drawer[open], cart-drawer.is-open, .js-cart-drawer[open]');
    return !!(overlay || monster || native);
  };

  /* ------------------------------------------------ reviews aggregate (F01) */
  // One authoritative aggregate per product, shared by the reviews section and the buy badge.
  // States: loading → positive | empty | unavailable. Missing/invalid data is *unavailable*, never 0.
  const aggregate = { state: 'loading', rating: null, count: null, productId: null };
  const aggregateLoads = {};
  window.landing.reviews = aggregate;
  const setAggregate = (patch) => {
    Object.assign(aggregate, patch);
    document.dispatchEvent(new CustomEvent('landing:reviews:aggregate', { detail: Object.assign({}, aggregate) }));
  };
  const loadStamped = (productId, apiKey, storeUrl, take) => {
    const k = String(productId);
    if (aggregateLoads[k]) return aggregateLoads[k];
    aggregate.productId = k;
    const url = `https://stamped.io/api/widget/reviews?productId=${encodeURIComponent(productId)}&apiKey=${encodeURIComponent(apiKey)}&storeUrl=${encodeURIComponent(storeUrl)}&take=${take || 12}&page=1&sortReviews=featured`;
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), 8000);
    aggregateLoads[k] = fetch(url, { headers: { Accept: 'application/json' }, signal: ac.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((json) => {
        clearTimeout(timer);
        // `total` / `rating` are Stamped's product-level aggregate, not derived from the cards.
        const total = Number(json && json.total);
        const rating = Number(json && json.rating);
        if (!Number.isFinite(total)) { setAggregate({ state: 'unavailable' }); }
        else if (total <= 0) { setAggregate({ state: 'empty', rating: null, count: 0 }); }
        else if (!Number.isFinite(rating) || rating <= 0) { setAggregate({ state: 'unavailable' }); }
        else { setAggregate({ state: 'positive', rating: Math.round(rating * 10) / 10, ratingText: (Math.round(rating * 10) / 10).toFixed(1), count: total }); }
        return json;
      })
      .catch(() => { clearTimeout(timer); setAggregate({ state: 'unavailable' }); return null; });
    return aggregateLoads[k];
  };
  const onAggregate = (root, fn) => {
    const { signal } = lifecycleFor(root).ac;
    document.addEventListener('landing:reviews:aggregate', (e) => fn(e.detail), { signal });
    if (aggregate.state !== 'loading') fn(Object.assign({}, aggregate));
  };
  const starGlyphs = (rating) => {
    const full = Math.round(Math.max(0, Math.min(5, Number(rating) || 0)));
    return '★'.repeat(full) + '☆'.repeat(5 - full);
  };

  /* ---------------------------------------------------------- buy section */
  const initBuy = (root) => {
    if (!initOnce(root, 'ldInit')) return;
    const { signal } = lifecycleFor(root).ac;
    const data = readJson(root.querySelector('[data-ld-variants]')) || { variants: [] };
    const byId = {};
    const byKey = {};
    data.variants.forEach((v) => { byId[v.id] = v; byKey[v.key] = v; });

    // The packshot sits beside the card (same section), not inside <product-form>.
    const section = root.closest('.ld-buy') || root;
    const packshot = section.querySelector('[data-ld-packshot]');
    const thumb = root.querySelector('[data-ld-packshot-thumb]');
    const bar = section.querySelector('[data-ld-sticky-bar]');
    const barPrice = bar && bar.querySelector('[data-ld-bar-price]');
    const barFlav = bar && bar.querySelector('[data-ld-bar-flav]');
    const flavLabel = root.querySelector('[data-ld-flav-label]');
    const stockEl = root.querySelector('[data-ld-stock]');
    const perServe = root.querySelector('[data-ld-per-serve]');
    const delivered = root.querySelector('[data-ld-delivered]');
    const servesEl = root.querySelector('[data-ld-serves]');
    const addBtn = root.querySelector('[name="add"]');
    const addedMsg = root.querySelector('[data-ld-added-msg]');
    const qtyInput = root.querySelector('[name="quantity"]');
    const productUrl = root.dataset.ldProductUrl || '';
    const currency = root.dataset.ldCurrency || 'AUD';
    const optionInputs = () => root.querySelectorAll('.option-selector input.opt-btn');
    let added = false;
    let current = null;
    let packshotToken = 0;

    const setAdded = (state) => {
      added = state;
      if (addedMsg) addedMsg.hidden = !state;
      if (!addBtn) return;
      if (state) {
        addBtn.textContent = addBtn.dataset.ldAddedText || 'Added — view cart';
      } else if (!addBtn.disabled) {
        addBtn.textContent = addBtn.dataset.addToCartText || 'Add to cart';
      }
    };

    // Every "read reviews on the product page" link carries the selected variant (F08).
    const updatePdpLinks = (v) => {
      if (!productUrl || !v) return;
      document.querySelectorAll('[data-ld-pdp-link="reviews"]').forEach((a) => {
        try {
          const u = new URL(productUrl, window.location.origin);
          u.searchParams.set('variant', String(v.id));
          u.hash = 'reviews';
          a.setAttribute('href', u.pathname + u.search + u.hash);
        } catch (e) { /* leave existing href */ }
      });
    };

    // Delivered total = price × quantity + the flat rate below the free threshold (re-audit #1).
    // Rate and threshold are merchant settings; nothing here assumes shipping is free.
    const thresholdCents = (() => {
      if (!delivered) return NaN;
      const m = (delivered.dataset.threshold || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
      return m ? Math.round(parseFloat(m[0]) * 100) : NaN;
    })();
    const paintDelivered = () => {
      if (!delivered || !current || !current.priceCents) return;
      const rate = parseInt(delivered.dataset.rateCents, 10) || 0;
      const qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);
      const subtotal = current.priceCents * qty;
      const free = !isNaN(thresholdCents) && subtotal >= thresholdCents;
      const total = subtotal + (free ? 0 : rate);
      const tpl = (free && delivered.dataset.textFree) ? delivered.dataset.textFree : delivered.dataset.text;
      delivered.textContent = (tpl || '{total}').replace('{total}', money(total));
      delivered.hidden = !current.available;
    };

    const paint = (v) => {
      if (!v) return;
      current = v;
      if (flavLabel) flavLabel.textContent = v.label + (v.available ? '' : ' — sold out');
      if (stockEl) {
        stockEl.textContent = v.available ? stockEl.dataset.inStock : stockEl.dataset.soldOut;
        stockEl.classList.toggle('is-soldout', !v.available);
      }
      if (perServe && v.perServe) perServe.textContent = v.perServe;
      if (servesEl && v.serves) servesEl.textContent = v.serves;
      paintDelivered();
      if (barPrice && v.price) barPrice.textContent = v.price;
      if (barFlav) barFlav.textContent = v.label + (barFlav.dataset.suffix || '');
      if (thumb && v.thumb) thumb.src = v.thumb;
      updatePdpLinks(v);
      // Keep the selected chip visible in the horizontally scrolling row (phones).
      const checkedInput = Array.from(optionInputs()).find((i) => i.checked);
      const chip = checkedInput && checkedInput.nextElementSibling;
      const row = chip && chip.closest('.option-selector__btns');
      if (row && row.scrollWidth > row.clientWidth) {
        const left = chip.offsetLeft - row.offsetLeft - 20;
        if (left < row.scrollLeft || chip.offsetLeft + chip.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = Math.max(0, left);
      }
      // Afterpay snippet context follows the selection (F09).
      if (window.ldAfterpayVariant && window.ldAfterpayVariant.id !== v.id) {
        window.ldAfterpayVariant = Object.assign({}, window.ldAfterpayVariant, { id: v.id, price: v.priceCents, available: v.available, title: v.label });
      }
      if (packshot && v.image && packshot.getAttribute('src') !== v.image) {
        // Guard against stale loads when flavours are switched quickly (F07.6).
        const token = ++packshotToken;
        packshot.classList.add('is-swapping');
        const img = new Image();
        img.onload = () => {
          if (token !== packshotToken) return;
          packshot.src = v.image;
          if (v.srcset) packshot.srcset = v.srcset; else packshot.removeAttribute('srcset');
          packshot.alt = v.alt || packshot.alt;
          packshot.classList.remove('is-swapping');
        };
        img.onerror = () => { if (token === packshotToken) packshot.classList.remove('is-swapping'); };
        img.src = v.image;
      }
    };

    // Theme variant picker → store
    root.addEventListener('on:variant:change', (e) => {
      const variant = e.detail && e.detail.variant;
      const v = variant ? byId[variant.id] : null;
      setAdded(false);
      if (v) {
        paint(v);
        store.set(v.key, v.label, 'buy');
        publish('landing_flavour_selected', { productId: data.productId || null, variantId: v.id, available: !!v.available });
      }
    }, { signal });

    // Store → theme variant picker (select the matching radio so price/availability stay native)
    onFlavour(({ key, source }) => {
      if (source === 'buy') return;
      const v = byKey[key];
      if (!v) return;
      const input = Array.from(optionInputs()).find((i) => keyOf(i.value) === key);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
      } else {
        paint(v);
      }
    }, root);

    // Added → view cart. The active cart app (Monster) intercepts the add, so the theme's
    // `on:cart:add` is not a reliable signal here: after a tap we also watch the real cart and
    // treat a changed line set as the add succeeding (F03.8). Nothing is marked "added" without it.
    const cartSignature = (c) => (c && Array.isArray(c.items) ? c.items.map((i) => i.key + ':' + i.quantity).join('|') : '');
    let cartSig = null;
    const readCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    readCart().then((c) => { cartSig = cartSignature(c); });
    const markAdded = () => {
      if (added) return;
      setAdded(true);
      publish('landing_added_to_cart', { variantId: current ? current.id : null });
      root.dispatchEvent(new CustomEvent('landing:cart:added', { bubbles: true }));
    };
    let watching = false;
    const watchCart = () => {
      if (watching) return;
      watching = true;
      const started = Date.now();
      const poll = () => readCart().then((c) => {
        const sig = cartSignature(c);
        if (c && cartSig !== null && sig !== cartSig) { cartSig = sig; watching = false; markAdded(); return; }
        if (Date.now() - started < 4000) setTimeout(poll, 500); else watching = false;
      });
      setTimeout(poll, 400);
    };
    root.addEventListener('on:cart:add', () => { readCart().then((c) => { cartSig = cartSignature(c); }); markAdded(); }, { signal });
    document.addEventListener('on:cart:change', () => { readCart().then((c) => { cartSig = cartSignature(c); }); }, { signal });
    // Opens whichever cart is active: Monster (exposes monster_refresh), else the theme drawer,
    // else the cart page. Never assumes the native drawer event is sufficient.
    const openActiveCart = () => {
      if (typeof window.monster_refresh === 'function') {
        try { window.monster_refresh(); } catch (e) { /* fall through */ }
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: addBtn } }));
      }
      setTimeout(() => {
        if (!drawerOpen()) window.location.href = (window.theme && theme.routes && theme.routes.cart) || '/cart';
      }, 700);
    };
    // Tap lock: the cart app submits the add itself, bypassing the theme's in-flight guard, so a
    // rapid double-tap would add twice (A10). One add per intended tap; the lock lifts when the
    // cart is seen to change or after 2.5s.
    let lockedUntil = 0;
    if (addBtn) {
      addBtn.addEventListener('click', (e) => {
        if (added) {
          e.preventDefault();
          e.stopImmediatePropagation();
          openActiveCart();
          return;
        }
        if (addBtn.disabled) return;
        const now = Date.now();
        if (now < lockedUntil) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        lockedUntil = now + 2500;
        addBtn.setAttribute('aria-busy', 'true');
        setTimeout(() => { if (Date.now() >= lockedUntil) addBtn.removeAttribute('aria-busy'); }, 2500);
        watchCart();
      }, { capture: true, signal });
    }
    root.addEventListener('landing:cart:added', () => { lockedUntil = 0; if (addBtn) addBtn.removeAttribute('aria-busy'); }, { signal });
    if (qtyInput) {
      qtyInput.addEventListener('change', () => { setAdded(false); paintDelivered(); }, { signal });
      qtyInput.addEventListener('input', paintDelivered, { signal });
      // the theme's ± buttons set the value programmatically; repaint after their click handler
      const qtyWrap = qtyInput.closest('quantity-wrapper');
      if (qtyWrap) qtyWrap.addEventListener('click', () => setTimeout(paintDelivered, 0), { signal });
    }

    // Rating badge (F01): explicit states from the shared aggregate; never a fake zero or five stars.
    const badge = root.querySelector('[data-ld-agg-badge]');
    if (badge) {
      const bStars = badge.querySelector('[data-ld-agg-badge-stars]');
      const bText = badge.querySelector('[data-ld-agg-badge-text]');
      onAggregate(root, (agg) => {
        badge.dataset.state = agg.state;
        if (agg.state === 'positive') {
          bStars.textContent = starGlyphs(agg.rating);
          bStars.setAttribute('aria-label', `${agg.ratingText || agg.rating} out of 5`);
          bStars.hidden = false;
          bText.textContent = `${agg.count} ${agg.count === 1 ? 'review' : 'reviews'}`;
          bText.hidden = false;
        } else if (agg.state === 'empty') {
          bStars.hidden = true;
          bText.textContent = 'No reviews yet';
          bText.hidden = false;
        } else {
          bStars.hidden = true;
          bText.hidden = true;
        }
      });
      if (aggregate.state === 'loading' && data.productId && root.dataset.ldStampedKey) {
        loadStamped(data.productId, root.dataset.ldStampedKey, root.dataset.ldStoreUrl, 12);
      }
    }

    // Mobile sticky bottom CTA (F03.4): visible after the hero, hidden only while the real add
    // action is on screen (so it never covers it) or while a cart/drawer overlay is open.
    if (bar) {
      const mqMobile = window.matchMedia('(max-width: 767.98px)');
      if (barFlav) {
        const m = barFlav.textContent.match(/(\s·\s.*)$/);
        barFlav.dataset.suffix = m ? m[1] : '';
      }
      const html = document.documentElement;
      let controlsSeen = false;
      const setBar = (show) => {
        bar.classList.toggle('is-off', !show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        html.classList.toggle('ld-bar-on', show);
        if (show) html.style.setProperty('--ld-bar-h', bar.offsetHeight + 'px');
      };
      const tickBar = () => {
        if (!mqMobile.matches) { setBar(false); return; }
        // Scroll-based (mobile V2): the hero is a pinned sticky banner, so its rect never leaves the viewport.
        const past = window.scrollY > window.innerHeight * 0.85;
        const vh = window.innerHeight;
        const headerH = parseInt(getComputedStyle(html).getPropertyValue('--theme-sticky-header-height'), 10) || 0;
        let actionVisible = false;
        if (addBtn) {
          const r = addBtn.getBoundingClientRect();
          actionVisible = r.bottom > headerH && r.top < vh;
        }
        if (actionVisible && !controlsSeen) { controlsSeen = true; publish('landing_purchase_controls_reached', {}); }
        setBar(past && !actionVisible && !drawerOpen());
      };
      addTicker(root, tickBar);
      tickBar();
    }

    // In-page links on the landing page scroll to header + 12px. The theme's generic smooth
    // scroll adds a further 100px, which pushed the add button below laptop and 667px-phone
    // folds (re-audit #9). `#buy` (old links) forwards to the purchase controls anchor (F03.1).
    const controlsId = root.id;
    if (controlsId && !document.documentElement.dataset.ldBuyAlias) {
      document.documentElement.dataset.ldBuyAlias = '1';
      const headerOffset = () => {
        const h = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--theme-sticky-header-height'), 10);
        return (isNaN(h) ? 64 : h) + 12;
      };
      // Layout position (offsetTop chain), not the rect: un-revealed elements sit 14px lower
      // in their reveal transform and would make the jump overshoot.
      const docTop = (el) => { let y = 0; for (let n = el; n; n = n.offsetParent) y += n.offsetTop; return y; };
      const goTo = (target) => {
        if (!target) return;
        const top = docTop(target) - headerOffset();
        window.scrollTo({ top: Math.max(0, top), left: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      const goControls = () => goTo(document.getElementById(controlsId));
      document.addEventListener('click', (e) => {
        const a = e.target.closest && e.target.closest('a[href^="#"]');
        if (!a || !a.closest('.ld') || a.hasAttribute('data-ld-open-nutrition')) return;
        const href = a.getAttribute('href');
        const target = href === '#buy' ? document.getElementById(controlsId) : document.querySelector(href);
        if (!target) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        goTo(target);
        if (href !== '#buy') { try { history.replaceState(null, '', href); } catch (err) { /* ignore */ } }
      }, { capture: true, signal });
      if (window.location.hash === '#buy') setTimeout(goControls, 300);
    }

    // Initial state: a valid ?variant= in the URL wins; unavailable ids stay selected but truthful (F07.5).
    let initial = null;
    try {
      const wanted = new URLSearchParams(window.location.search).get('variant');
      if (wanted && byId[wanted]) {
        const input = Array.from(optionInputs()).find((i) => keyOf(i.value) === byId[wanted].key);
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false }));
        }
        initial = byId[wanted];
      }
    } catch (e) { /* ignore malformed URLs */ }
    if (!initial) {
      const checked = Array.from(optionInputs()).find((i) => i.checked);
      initial = (checked && byKey[keyOf(checked.value)]) || byId[root.dataset.ldCurrentVariant] || data.variants[0];
    }
    if (initial) {
      paint(initial);
      store.set(initial.key, initial.label, 'buy');
    }

    // Product view signal for this landing page (once per document, bounded payload) — F11.
    if (!document.documentElement.dataset.ldViewed && initial) {
      document.documentElement.dataset.ldViewed = '1';
      publish('landing_product_viewed', {
        productId: data.productId || null,
        variantId: initial.id,
        price: initial.priceCents || null,
        currency,
        url: window.location.pathname
      });
    }

    // Shipping estimate against the real cart (F04). Only offered once the cart has items.
    const ship = root.querySelector('[data-ld-ship]');
    if (ship) initShipping(ship, root, currency);
  };

  /* ------------------------------------------------- shipping estimator (F04) */
  const initShipping = (ship, root, currency) => {
    const { signal } = lifecycleFor(root).ac;
    const form = ship.querySelector('[data-ld-ship-form]');
    const zip = ship.querySelector('[data-ld-ship-zip]');
    const state = ship.querySelector('[data-ld-ship-state]');
    const result = ship.querySelector('[data-ld-ship-result]');
    const msgs = ship.dataset;
    let cart = null;
    let quote = null; // { zip, subtotal, rates }
    let busy = false;

    const say = (html) => { result.innerHTML = html; };
    const refreshCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => {
        cart = c;
        const has = !!(c && c.item_count > 0);
        ship.hidden = !has;
        if (has && quote && quote.subtotal !== c.items_subtotal_price) {
          quote = null;
          say(`<p class="ld-ship__note">${esc(msgs.stale || '')}</p>`);
        }
        return c;
      })
      .catch(() => null);

    const render = (rates, subtotal) => {
      if (!rates.length) { say(`<p class="ld-ship__note">${esc(msgs.none || '')}</p>`); return; }
      const rows = rates.map((r) => {
        const cents = Math.round(parseFloat(r.price) * 100);
        const total = subtotal + cents;
        const eta = r.delivery_days && r.delivery_days.length ? ` · ${r.delivery_days.length > 1 && r.delivery_days[0] !== r.delivery_days[r.delivery_days.length - 1] ? `${r.delivery_days[0]}–${r.delivery_days[r.delivery_days.length - 1]}` : r.delivery_days[0]} business days` : '';
        return `<li class="ld-ship__rate"><span class="ld-ship__rate-name">${esc(r.name)}${esc(eta)}</span><span class="ld-ship__rate-price">${cents === 0 ? 'Free' : esc(money(cents, r.currency || currency))}</span><span class="ld-ship__rate-total">Estimated total ${esc(money(total, currency))}</span></li>`;
      }).join('');
      say(`<p class="ld-ship__note">Subtotal ${esc(money(subtotal, currency))} · estimate for ${esc(quote.zip)}${quote.province ? ', ' + esc(quote.province) : ''}. Final shipping is confirmed at checkout.</p><ul class="ld-ship__rates">${rows}</ul>`);
    };

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (busy) return;
      const value = (zip.value || '').trim();
      if (!value) { say(`<p class="ld-ship__note">${esc(msgs.empty || '')}</p>`); zip.focus(); return; }
      if (!/^[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/.test(value)) { say(`<p class="ld-ship__note">${esc(msgs.invalid || '')}</p>`); zip.focus(); return; }
      busy = true;
      ship.classList.add('is-busy');
      say('<p class="ld-ship__note">Checking shipping options…</p>');
      publish('landing_shipping_estimate_requested', {}); // no postcode is sent
      const province = state ? state.value : (msgs.province || '');
      if (state && !province) { say(`<p class="ld-ship__note">${esc(msgs.state || 'Select your state or territory.')}</p>`); state.focus(); return; }
      const params = new URLSearchParams();
      params.set('shipping_address[zip]', value);
      params.set('shipping_address[country]', msgs.country || 'Australia');
      if (province) params.set('shipping_address[province]', province);
      refreshCart().then((c) => {
        if (!c || !c.item_count) { ship.hidden = true; return null; }
        return fetch('/cart/shipping_rates.json?' + params.toString(), { headers: { Accept: 'application/json' } })
          .then((res) => res.json().then((json) => ({ ok: res.ok, status: res.status, json })))
          .then(({ ok, json }) => {
            if (!ok || !json || !Array.isArray(json.shipping_rates)) {
              const firstErr = json && typeof json === 'object' ? Object.keys(json).map((k) => Array.isArray(json[k]) ? json[k].join(', ') : (typeof json[k] === 'string' ? json[k] : '')).find(Boolean) : '';
              const err = firstErr;
              say(`<p class="ld-ship__note">${esc(err ? String(err) : (msgs.error || ''))}</p>`);
              return;
            }
            quote = { zip: value, province, subtotal: c.items_subtotal_price, rates: json.shipping_rates };
            render(json.shipping_rates, c.items_subtotal_price);
          });
      }).catch(() => say(`<p class="ld-ship__note">${esc(msgs.error || '')}</p>`))
        .then(() => { busy = false; ship.classList.remove('is-busy'); });
    }, { signal });

    root.addEventListener('on:cart:add', () => { setTimeout(refreshCart, 300); }, { signal });
    root.addEventListener('landing:cart:added', () => { refreshCart(); }, { signal });
    document.addEventListener('on:cart:change', () => { setTimeout(refreshCart, 300); }, { signal });
    refreshCart();
  };

  /* --------------------------------------------------------- lab results */
  const initResults = (root) => {
    if (!initOnce(root, 'ldInit')) return;
    const { signal } = lifecycleFor(root).ac;
    const data = readJson(root.querySelector('[data-ld-reports]')) || { reports: [] };
    const reports = data.reports || [];
    if (!reports.length) return;
    const byKey = {};
    reports.forEach((r) => { byKey[r.key] = r; });
    const strings = data.strings || {};

    const q = (sel) => root.querySelector(sel);
    const qa = (sel) => root.querySelectorAll(sel);
    const rep = (name) => root.querySelectorAll('[data-ld-rep="' + name + '"]');
    const setText = (name, val) => rep(name).forEach((el) => { el.textContent = val; });

    const annot = q('[data-ld-annot]');
    const svgHost = q('[data-ld-annot-svg]');
    const mqWide = window.matchMedia('(min-width: 880px)');

    const noteText = (i, r) => {
      const hasServe = !!(r.serveG && r.decl);
      const perServe = hasServe ? fmt1(r.p * r.serveG / 100) : null;
      switch (i) {
        case 3: return `${fmt1(r.p)} g of protein in every 100 g of powder—weighed as received, moisture and all. No dry-basis inflation (more on that below).`;
        case 4: return hasServe
          ? `× this flavour's ${r.serveG} g scoop = ${perServe} g per serve—beside the ${r.decl} g its panel declares.`
          : 'Your flavour’s panel shows its own scoop weight—serving weights vary by flavour. Multiply to get the per-serve figure and compare with the declared value.';
        default: return null;
      }
    };

    const drawLinks = () => {
      if (!annot || !svgHost) return;
      if (!mqWide.matches) { svgHost.innerHTML = ''; return; }
      const wr = annot.getBoundingClientRect();
      if (!wr.width) return;
      const posRail = (railId, ids) => {
        const rail = q('[data-ld-rail="' + railId + '"]');
        if (!rail) return;
        const rr = rail.getBoundingClientRect();
        let last = -1e9;
        ids.forEach((i) => {
          const n = q('[data-ld-nt="' + i + '"]');
          const m = q('[data-ld-mk="' + i + '"]');
          if (!n || !m) return;
          const mr = m.getBoundingClientRect();
          let top = mr.top + mr.height / 2 - rr.top - 12;
          if (top < last + 16) top = last + 16;
          n.style.top = top + 'px';
          last = top + n.getBoundingClientRect().height;
        });
      };
      posRail('l', [1, 3, 5]);
      posRail('r', [2, 4, 6]);
      let paths = '';
      for (let i = 1; i <= 6; i += 1) {
        const m = q('[data-ld-mk="' + i + '"]');
        const n = q('[data-ld-nt="' + i + '"]');
        if (!m || !n) continue;
        const mr = m.getBoundingClientRect();
        const nr = n.getBoundingClientRect();
        const left = i % 2 === 1;
        const x2 = (left ? mr.left - 7 : mr.right + 7) - wr.left;
        const y2 = mr.top + mr.height / 2 - wr.top;
        const x1 = (left ? nr.right + 5 : nr.left - 5) - wr.left;
        const y1 = nr.top + 14 - wr.top;
        const mx = (x1 + x2) / 2;
        const d = `M${x1},${y1} C${mx},${y1 + 4} ${mx},${y2 - 4} ${x2},${y2}`;
        const ah = left
          ? `M${x2 - 8},${y2 - 5} L${x2},${y2} L${x2 - 8},${y2 + 5}`
          : `M${x2 + 8},${y2 - 5} L${x2},${y2} L${x2 + 8},${y2 + 5}`;
        paths += `<path d="${d}" fill="none" stroke="#C4453A" stroke-width="1.75" stroke-linecap="round" opacity=".7"/><path d="${ah}" fill="none" stroke="#C4453A" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" opacity=".7"/>`;
      }
      svgHost.innerHTML = `<svg width="${wr.width}" height="${wr.height}" style="display:block; overflow:visible" aria-hidden="true">${paths}</svg>`;
    };
    const scheduleDraw = () => requestAnimationFrame(drawLinks);

    const replayRing = () => {
      if (reduceMotion) return;
      qa('[data-ld-ring] path').forEach((path) => {
        path.parentNode.replaceChild(path.cloneNode(true), path);
      });
    };

    const render = (r) => {
      if (!r) return;
      const hasServe = !!(r.serveG && r.decl);
      const perServe = hasServe ? fmt1(r.p * r.serveG / 100) : null;

      setText('id', r.id);
      setText('date', r.date);
      setText('order', r.order);
      setText('sub', r.sub);
      setText('flavour', r.flavour);
      setText('lot', r.lot);
      setText('start', r.start);
      setText('p', fmt1(r.p));
      setText('f', fmt1(r.f));
      rep('pdf').forEach((a) => { a.href = r.pdf || '#'; a.hidden = !r.pdf; });

      // meta grid
      const meta = q('[data-ld-meta]');
      if (meta) {
        const rows = r.sampled
          ? [['Sample code', r.sample], ['Reception date', r.recv], ['Product Type', r.productType || 'Protein powder'], ['Lot Number', r.lot], ['Best before date', r.bbd], ['Manufactured Date', r.manufactured], ['Sampled Date & Time', r.sampled], ['Sampler(s)', r.sampler]]
          : [['Sample code', r.sample], ['Reception date', r.recv], ['Reception temperature', r.temp], ['Product Type', r.productType || 'Protein powder'], ['Lot Number', r.lot], ['Best before date', r.bbd]];
        meta.innerHTML = rows.filter((x) => x[1]).map(([k, v]) => `<div><span class="k">${esc(k)}:</span> <span class="v">${esc(v)}</span></div>`).join('');
      }

      // summary rows
      const rows = hasServe
        ? [['Reported by Eurofins, as received', `${fmt1(r.p)} g per 100 g`], [`Calculated for a ${r.serveG} g serve`, `${perServe} g per serve`], ['Declared on the nutrition panel shown', `${r.decl} g per serve`]]
        : [['Reported by Eurofins, as received', `${fmt1(r.p)} g per 100 g`]];
      const rowsHtml = rows.map(([k, v]) => `<div class="ld-sheet__summary-row"><span class="k">${esc(k)}</span><span class="v">${esc(v)}</span></div>`).join('');
      qa('[data-ld-summary-rows]').forEach((sum) => { sum.innerHTML = rowsHtml; });
      const summaryNote = hasServe
        ? (strings.summaryServe || 'The laboratory reported the per-100 g result. We calculated the per-serve figure using this flavour’s serving weight so you can compare it with the label.')
        : (strings.summaryNoServe || 'Serving weights vary by flavour. Your flavour’s panel shows its scoop weight and declared protein—multiply the reported result to compare.');
      qa('[data-ld-summary-note]').forEach((el) => { el.textContent = summaryNote; });

      // annotations with live numbers (notes 3 and 4)
      [3, 4].forEach((i) => {
        const t = noteText(i, r);
        root.querySelectorAll('[data-ld-note="' + i + '"]').forEach((el) => { el.textContent = t; });
      });

      // as received vs dry basis
      qa('[data-ld-asis-bar]').forEach((el) => { el.style.width = Math.round(r.p) + '%'; });
      qa('[data-ld-dry-bar]').forEach((el) => { el.style.width = Math.min(100, Math.round(r.p / 0.95)) + '%'; });
      setText('dry', fmt1(r.p / 0.95));

      // selector state
      root.querySelectorAll('[data-ld-flav-btn]').forEach((b) => {
        b.setAttribute('aria-pressed', keyOf(b.dataset.key) === r.key ? 'true' : 'false');
      });
      qa('[data-ld-flavsel-label]').forEach((el) => { el.textContent = r.flavour; });

      replayRing();
      scheduleDraw();
      setTimeout(drawLinks, 350);
    };

    // selector → store
    root.querySelectorAll('[data-ld-flav-btn]').forEach((b) => {
      b.addEventListener('click', () => {
        const r = byKey[keyOf(b.dataset.key)];
        if (!r) return;
        if (r.key === store.key) { render(r); return; }
        store.set(r.key, r.flavour, 'results');
      });
    });

    // store → facsimile
    onFlavour(({ key }) => {
      const r = byKey[key];
      if (r) render(r);
    }, root);

    // lot lookup
    const input = q('[data-ld-lot-input]');
    const btn = q('[data-ld-lot-btn]');
    const msg = q('[data-ld-lot-msg]');
    const norm = (s) => String(s || '').trim().toUpperCase().replace(/O/g, '0').replace(/\s+/g, '');
    const say = (t) => { if (msg) { msg.textContent = t; msg.hidden = !t; } };
    const find = () => {
      const val = norm(input && input.value);
      if (!val) { say(strings.lookupEmpty || ''); return; }
      const hit = reports.find((r) => norm(r.lot) === val);
      if (hit) {
        store.set(hit.key, hit.flavour, 'lookup');
        render(hit);
        say((strings.lookupHit || 'Found it — showing Eurofins report {report} for lot {lot} ({flavour}) above.')
          .replace('{report}', hit.id).replace('{lot}', hit.lot).replace('{flavour}', hit.flavour));
        const target = document.getElementById('results') || root;
        window.scrollTo({ top: target.getBoundingClientRect().top + window.scrollY - 70, behavior: reduceMotion ? 'auto' : 'smooth' });
      } else {
        say(strings.lookupMiss || '');
      }
    };
    if (btn) btn.addEventListener('click', find);
    if (input) {
      input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); find(); } });
      input.addEventListener('input', () => say(''));
    }

    // layout
    let lastW = window.innerWidth;
    addTicker(root, () => { if (window.innerWidth !== lastW) { lastW = window.innerWidth; drawLinks(); } });
    if (mqWide.addEventListener) mqWide.addEventListener('change', scheduleDraw, { signal });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleDraw);
    setTimeout(drawLinks, 700);
    setTimeout(drawLinks, 1800);

    // initial: follow the store if it already has a matching flavour, else first report
    const initial = (store.key && byKey[store.key]) || reports[0];
    render(initial);
    if (!store.key) store.set(initial.key, initial.flavour, 'results');
  };

  /* ----------------------------------------------------------- nutrition */
  const initNutrition = (root) => {
    if (!initOnce(root, 'ldInit')) return;
    const panels = root.querySelectorAll('[data-ld-nip-panel]');
    const none = root.querySelector('[data-ld-nip-none]');
    const noneLabel = root.querySelector('[data-ld-nip-none-label]');
    const apply = (key, label) => {
      let hit = false;
      panels.forEach((p) => {
        const match = keyOf(p.dataset.key) === key;
        p.hidden = !match;
        if (match) hit = true;
      });
      if (none) none.hidden = hit;
      if (noneLabel && label) noneLabel.textContent = label;
    };
    onFlavour(({ key, label }) => apply(key, label), root);
    if (store.key) apply(store.key, store.label);
    else {
      const first = panels[0];
      if (first) apply(keyOf(first.dataset.key), first.dataset.label);
    }

    // "View ingredients and nutrition" links open the accordion before the theme smooth-scrolls
    const details = root.querySelector('details');
    document.querySelectorAll('[data-ld-open-nutrition]').forEach((a) => {
      a.addEventListener('click', () => { if (details) details.open = true; });
    });
  };

  /* ------------------------------------------------------------- reviews */
  const initReviews = (root) => {
    if (!initOnce(root, 'ldInit')) return;
    const { signal } = lifecycleFor(root).ac;
    const track = root.querySelector('[data-ld-review-track]');
    const marquee = root.querySelector('[data-ld-review-marquee]');
    const controls = root.querySelector('[data-ld-review-controls]');
    const pauseBtn = root.querySelector('[data-ld-review-pause]');
    const { productId, apiKey, storeUrl } = root.dataset;
    const take = parseInt(root.dataset.take, 10) || 12;
    const ds = root.dataset;
    const mqMobile = window.matchMedia('(max-width: 767.98px)');

    // --- aggregate header (F01): explicit states, nothing numeric until known
    const aggStars = root.querySelector('[data-ld-agg-stars]');
    const aggHeading = root.querySelector('[data-ld-agg-heading]');
    const aggText = root.querySelector('[data-ld-agg-text]');
    const aggSep = root.querySelector('[data-ld-agg-sep]');
    const fill = (tpl, agg) => String(tpl || '').replace('{rating}', agg.ratingText || agg.rating).replace('{count}', agg.count);
    const renderAggregate = (agg) => {
      root.dataset.state = agg.state;
      if (!aggHeading || !aggStars) return;
      if (agg.state === 'positive') {
        aggStars.textContent = starGlyphs(agg.rating);
        aggStars.setAttribute('aria-label', `${agg.ratingText || agg.rating} out of 5`);
        aggStars.hidden = false;
        aggHeading.textContent = agg.rating >= 5 ? ds.headingFive : fill(ds.headingOther, agg);
        if (aggText) aggText.textContent = fill(ds.subPositive, agg);
        if (aggSep) aggSep.hidden = false;
      } else if (agg.state === 'empty') {
        aggStars.hidden = true;
        aggHeading.textContent = ds.headingEmpty || ds.headingNone;
        if (aggText) aggText.textContent = ds.subEmpty || '';
        if (aggSep) aggSep.hidden = false;
      } else if (agg.state === 'unavailable') {
        aggStars.hidden = true;
        aggHeading.textContent = ds.headingNone;
        if (aggText) aggText.textContent = ds.subUnavailable || '';
        if (aggSep) aggSep.hidden = false;
      }
    };
    if (root.dataset.state !== 'positive') onAggregate(root, renderAggregate);

    if (!productId || !apiKey || !storeUrl) {
      if (marquee) marquee.hidden = true;
      setAggregate({ state: 'unavailable' });
      return;
    }

    // --- cards
    const nameOf = (author) => {
      const parts = String(author || '').trim().split(/\s+/).filter(Boolean);
      if (!parts.length) return 'Customer';
      if (parts.length === 1) return parts[0];
      return `${parts[0]} ${parts[parts.length - 1].charAt(0).toUpperCase()}.`;
    };
    const card = (r, dup) => {
      // Stamped: reviewVerifiedType 2 = verified buyer. Individual badge only when the source says so.
      const verified = Number(r.reviewVerifiedType) >= 2;
      const text = (r.reviewMessage || r.reviewTitle || '').trim();
      const rating = Math.max(0, Math.min(5, Math.round(Number(r.reviewRating) || 0)));
      return `<div class="ld-review" role="listitem"${dup ? ' aria-hidden="true" data-dup="1"' : ''}>
        <div class="ld-review__stars" role="img" aria-label="${rating} out of 5">${'★'.repeat(rating)}</div>
        <div class="ld-review__quote">“${esc(text)}”</div>
        <div class="ld-review__foot">
          <span class="ld-review__name">${esc(nameOf(r.author))}</span>
          ${verified ? `<span class="ld-review__verified">${esc(ds.verifiedLabel || 'Verified buyer')}</span>` : ''}
        </div>
      </div>`;
    };

    loadStamped(productId, apiKey, storeUrl, take).then((json) => {
      if (!track || !marquee) return;
      const list = (json && (json.data || json.reviews)) || [];
      const reviews = list.filter((r) => (r.reviewMessage || r.reviewTitle) && Number(r.reviewRating) > 0);
      if (!reviews.length) { marquee.hidden = true; return; }
      const setHtml = reviews.map((r) => card(r, false)).join('');
      marquee.hidden = false;

      if (reduceMotion) {
        // Reduced motion: a static, swipeable list — no auto-motion (F10.6). Phones otherwise
        // get the auto-sliding marquee of the mobile V2 design (pauses while touched).
        track.innerHTML = setHtml;
        track.style.animation = 'none';
        track.style.width = 'auto';
        marquee.classList.add('is-static');
        return;
      }
      const viewport = marquee.clientWidth || window.innerWidth;
      track.innerHTML = setHtml;
      const setWidth = track.scrollWidth;
      if (setWidth <= viewport) {
        track.style.animation = 'none';
        track.style.width = 'auto';
        marquee.classList.add('is-static');
        return;
      }
      // The loop translates by -50%: an even number of identical sets, ≥ two viewports wide.
      // Duplicate sets are decorative and hidden from assistive technology.
      let sets = 2;
      while (setWidth * sets < viewport * 2 && sets < 8) sets += 2;
      let html = '';
      for (let i = 0; i < sets; i += 1) html += reviews.map((r) => card(r, i > 0)).join('');
      track.innerHTML = html;
      track.style.animationDuration = Math.round((track.scrollWidth / 2) / (mqMobile.matches ? 40 : 55)) + 's';
      // Touch = pause (no hover on phones); resumes shortly after the finger lifts.
      let touchTimer = 0;
      track.addEventListener('touchstart', () => { clearTimeout(touchTimer); track.classList.add('is-touch-paused'); }, { passive: true, signal });
      track.addEventListener('touchend', () => { touchTimer = setTimeout(() => track.classList.remove('is-touch-paused'), 1200); }, { passive: true, signal });

      if (controls && pauseBtn) {
        controls.hidden = false;
        const setPaused = (paused) => {
          track.style.animationPlayState = paused ? 'paused' : 'running';
          pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
          pauseBtn.textContent = paused ? (pauseBtn.dataset.playLabel || 'Play') : (pauseBtn.dataset.pauseLabel || pauseBtn.textContent);
        };
        pauseBtn.dataset.pauseLabel = pauseBtn.textContent;
        pauseBtn.addEventListener('click', () => setPaused(pauseBtn.getAttribute('aria-pressed') !== 'true'), { signal });
        // Keyboard focus inside the strip pauses it so links/text stay reachable.
        track.addEventListener('focusin', () => setPaused(true), { signal });
      }
    });
  };

  /* ---------------------------------------------------------------- init */
  const init = (scope) => {
    const q = (sel) => (scope || document).querySelectorAll(sel);
    q('.ld-header').forEach(initHeader);
    // Buy first so its selected variant seeds the store, then the rest.
    q('[data-ld-buy]').forEach(initBuy);
    q('[data-ld-results]').forEach(initResults);
    q('[data-ld-nutrition]').forEach(initNutrition);
    q('[data-ld-reviews]').forEach(initReviews);
    initReveals();
  };

  // Engagement signal for report PDFs (bounded, no lot/postcode/personal data).
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('[data-ld-track]');
    if (a) publish('landing_' + a.dataset.ldTrack, {});
  });

  // The theme's <variant-picker> is defined in a deferred script; wait for it so the
  // initial checked radio and `on:variant:change` are reliable.
  const ready = () => {
    const vp = document.querySelector('.ld-buy variant-picker');
    if (vp && window.customElements && !customElements.get('variant-picker')) {
      customElements.whenDefined('variant-picker').then(() => init(), () => init());
    } else {
      init();
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready);
  else ready();

  // Theme editor: tear down and re-init only the (re)loaded landing section.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:unload', (e) => { if (e.target) teardown(e.target); });
    document.addEventListener('shopify:section:load', (e) => {
      const s = e.target;
      if (!s || !s.querySelector('.ld')) return;
      teardown(s);
      init(s);
    });
  }
})();
